import { useState } from "react";
import PropTypes from "prop-types";
import { QRCodeSVG } from "qrcode.react";
import {
  Smartphone,
  Copy,
  Check,
  Shield,
  Download,
  AlertCircle,
} from "lucide-react";
import { Button } from "@shared/components/base/Button";
import { authService } from "@shared/core/services/AuthService";
import { TYPOGRAPHY, THEME } from "@shared/constants/designSystem";
import { cn } from "@shared/utils";

/**
 * MFAEnrollment Component
 *
 * Multi-step enrollment flow for Multi-Factor Authentication using TOTP.
 * Guides users through:
 * 1. Initial: Start enrollment
 * 2. Scan: Display QR code and manual key
 * 3. Verify: Enter 6-digit code from authenticator
 * 4. Recovery: Display and save recovery codes
 * 5. Complete: Success message
 */
export function MFAEnrollment({ onComplete, onCancel }) {
  const [enrollmentStep, setEnrollmentStep] = useState("initial");
  const [qrCodeUri, setQrCodeUri] = useState("");
  const [manualKey, setManualKey] = useState("");
  const [factorId, setFactorId] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const handleStartEnrollment = async () => {
    setIsLoading(true);
    setError("");

    try {
      // Check for existing factors and clean them ALL up
      // This ensures we start fresh, even if previous enrollment was incomplete
      const existingFactors = await authService.listMFAFactors();

      // Remove ALL existing factors (both verified and unverified)
      // This component is only shown when user wants to enable MFA,
      // so it's safe to clean up any leftover factors
      for (const factor of existingFactors) {
        try {
          await authService.unenrollMFA(factor.id);
        } catch (cleanupErr) {
          console.warn("Failed to clean up factor:", cleanupErr);
          // Continue anyway - we'll try to enroll
        }
      }

      // Now proceed with enrollment
      const enrollment = await authService.enrollMFA();
      setQrCodeUri(enrollment.qrCodeUri);
      setManualKey(enrollment.secret);
      setFactorId(enrollment.factorId);
      setEnrollmentStep("scan");
    } catch (err) {
      setError(err.message || "Failed to start MFA enrollment");
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      await authService.verifyMFAEnrollment(factorId, verificationCode);
      const codes = await authService.generateRecoveryCodes();
      setRecoveryCodes(codes);
      setEnrollmentStep("recovery");
    } catch (err) {
      setError(err.message || "Invalid verification code. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyKey = () => {
    navigator.clipboard.writeText(manualKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadCodes = () => {
    // Get site name from URL or use domain as fallback
    const siteName = window.location.hostname;
    const timestamp = new Date().toLocaleDateString();

    const content = `Two-Factor Authentication Recovery Codes
Site: ${siteName}
Generated: ${timestamp}

${recoveryCodes.join("\n")}

IMPORTANT: Store these codes securely. Each code can only be used once.
If you lose access to your authenticator app, you can use these codes to sign in.`;

    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `mfa-recovery-codes-${siteName}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleComplete = () => {
    setEnrollmentStep("complete");
    setTimeout(() => {
      if (onComplete) onComplete();
    }, 2000);
  };

  return (
    <div className={`${THEME.COLORS.BG.DEFAULT} rounded-lg p-6 space-y-6`}>
      {/* Initial Step */}
      {enrollmentStep === "initial" && (
        <div className="text-center space-y-4">
          <div className="flex justify-center">
            <div className={`${THEME.COLORS.BG.ACCENT} rounded-full p-4`}>
              <Shield className="w-8 h-8 text-primary-600" />
            </div>
          </div>
          <h3 className={cn(TYPOGRAPHY.HEADING.H3, "text-center")}>
            Enable Two-Factor Authentication
          </h3>
          <p
            className={cn(TYPOGRAPHY.BODY, "text-gray-600 dark:text-gray-400")}
          >
            Add an extra layer of security to your account. You&apos;ll need an
            authenticator app like Google Authenticator, Microsoft
            Authenticator, or Authy.
          </p>
          <div className="flex gap-3 justify-center">
            <Button onClick={handleStartEnrollment} disabled={isLoading}>
              {isLoading ? "Starting..." : "Get Started"}
            </Button>
            {onCancel && (
              <Button variant="outline" onClick={onCancel}>
                Cancel
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Scan Step */}
      {enrollmentStep === "scan" && (
        <div className="space-y-6">
          <div>
            <h3 className={cn(TYPOGRAPHY.HEADING.H3, "mb-2")}>Scan QR Code</h3>
            <p
              className={cn(
                TYPOGRAPHY.BODY,
                "text-gray-600 dark:text-gray-400",
              )}
            >
              Scan this QR code with your authenticator app
            </p>
          </div>

          <div className="flex justify-center p-6 bg-white rounded-lg">
            <QRCodeSVG value={qrCodeUri} size={256} level="L" />
          </div>

          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <div className="flex items-start gap-2 mb-2">
              <Smartphone className="w-5 h-5 text-gray-500 mt-1" />
              <div className="flex-1">
                <p className={cn(TYPOGRAPHY.LABEL, "mb-1")}>
                  Or enter this key manually:
                </p>
                <div className="flex gap-2">
                  <code
                    className={`flex-1 ${THEME.COLORS.BG.ACCENT} px-3 py-2 rounded font-mono text-sm break-all`}
                  >
                    {manualKey}
                  </code>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleCopyKey}
                    title="Copy key"
                  >
                    {copied ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <Button
            onClick={() => setEnrollmentStep("verify")}
            className="w-full"
          >
            Continue
          </Button>
        </div>
      )}

      {/* Verify Step */}
      {enrollmentStep === "verify" && (
        <form onSubmit={handleVerifyCode} className="space-y-6">
          <div>
            <h3 className={cn(TYPOGRAPHY.HEADING.H3, "mb-2")}>Verify Code</h3>
            <p
              className={cn(
                TYPOGRAPHY.BODY,
                "text-gray-600 dark:text-gray-400",
              )}
            >
              Enter the 6-digit code from your authenticator app
            </p>
          </div>

          <div>
            <label htmlFor="verification-code" className={TYPOGRAPHY.LABEL}>
              Verification Code
            </label>
            <input
              id="verification-code"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={verificationCode}
              onChange={(e) =>
                setVerificationCode(e.target.value.replace(/\D/g, ""))
              }
              className={`mt-1 block w-full px-4 py-3 text-center text-2xl font-mono tracking-widest ${THEME.COLORS.BG.INPUT} border ${THEME.COLORS.BORDER.DEFAULT} rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent`}
              placeholder="000000"
              autoFocus
              required
            />
          </div>

          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <p
                className={cn(
                  TYPOGRAPHY.BODY,
                  "text-red-800 dark:text-red-200",
                )}
              >
                {error}
              </p>
            </div>
          )}

          <div className="flex gap-3">
            <Button
              type="submit"
              disabled={isLoading || verificationCode.length !== 6}
              className="flex-1"
            >
              {isLoading ? "Verifying..." : "Verify"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setEnrollmentStep("scan")}
            >
              Back
            </Button>
          </div>
        </form>
      )}

      {/* Recovery Codes Step */}
      {enrollmentStep === "recovery" && (
        <div className="space-y-6">
          <div>
            <h3 className={cn(TYPOGRAPHY.HEADING.H3, "mb-2")}>
              Save Recovery Codes
            </h3>
            <p
              className={cn(
                TYPOGRAPHY.BODY,
                "text-gray-600 dark:text-gray-400",
              )}
            >
              Store these codes securely. You&apos;ll need them if you lose
              access to your authenticator app. Each code can only be used once.
            </p>
          </div>

          <div className={`${THEME.COLORS.BG.ACCENT} p-4 rounded-lg`}>
            <div className="grid grid-cols-2 gap-2 font-mono text-sm">
              {recoveryCodes.map((code, index) => (
                <div key={index} className="flex items-center gap-2">
                  <span className="text-gray-500 w-6">{index + 1}.</span>
                  <code className="flex-1">{code}</code>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              onClick={handleDownloadCodes}
              variant="outline"
              className="flex-1"
            >
              <Download className="w-4 h-4 mr-2" />
              Download Codes
            </Button>
            <Button onClick={handleComplete} className="flex-1">
              I&apos;ve Saved My Codes
            </Button>
          </div>

          <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
            <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <p
              className={cn(
                TYPOGRAPHY.CAPTION,
                "text-amber-800 dark:text-amber-200",
              )}
            >
              Important: Store these codes in a secure location. You won&apos;t
              be able to see them again.
            </p>
          </div>
        </div>
      )}

      {/* Complete Step */}
      {enrollmentStep === "complete" && (
        <div className="text-center space-y-4 py-8">
          <div className="flex justify-center">
            <div className="bg-green-100 dark:bg-green-900/30 rounded-full p-4">
              <Check className="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>
          </div>
          <h3 className={cn(TYPOGRAPHY.HEADING.H3)}>
            Two-Factor Authentication Enabled!
          </h3>
          <p
            className={cn(TYPOGRAPHY.BODY, "text-gray-600 dark:text-gray-400")}
          >
            Your account is now protected with two-factor authentication.
          </p>
        </div>
      )}
    </div>
  );
}

MFAEnrollment.propTypes = {
  onComplete: PropTypes.func,
  onCancel: PropTypes.func,
};
